import React, { useState } from 'react';
import { Input } from 'reactstrap';
import Select, { MultiValue } from 'react-select';
import { useTranslation } from 'react-i18next';
import { Control, Controller, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form';
import 'react-datepicker/dist/react-datepicker.css';

import { FeedbackPerformed, IEntityReference } from 'models/Order';
import { IOption } from 'models/Form';
import './FormComponents.css';

export interface ICustomSelectProps<T extends FieldValues, TOption> {
  control: Control<T, object>;
  fieldName: FieldPath<T>;
  options: Array<IOption<TOption>>;
  label: string;
  placeholder?: string;
  getOptionLabel: (item: IOption<TOption>) => string;
  getOptionValue: (item: IOption<TOption>) => string;
  isMulti?: boolean;
  readonly?: boolean;
  isOnlyValue?: boolean;
  hideOptionsUntilSearch?: boolean;
  rules?: Omit<
    RegisterOptions<T, FieldPath<T>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
}

export function ControlledSelect<T extends FieldValues, TOption>({
  fieldName,
  control,
  options,
  label,
  rules,
  placeholder,
  getOptionLabel,
  getOptionValue,
  isMulti,
  isOnlyValue,
  readonly,
  hideOptionsUntilSearch = false,
}: ICustomSelectProps<T, TOption>) {
  const { t } = useTranslation('translation');

  const getOptionFromValue = (value: IEntityReference, includeDeprecated?: boolean) => {
    let candidates: IOption<TOption>[] = [];
    if (typeof value !== 'object') {
      candidates = options.filter(
        (option) => option.value === value && (includeDeprecated || !option.isDeprecated)
      );
    } else {
      candidates = options.filter(
        (option) => option.value === value.id && (includeDeprecated || !option.isDeprecated)
      );
    }

    if (candidates.length === 1) {
      return candidates[0];
    }
    return undefined;
  };

  const availableOptions = options.filter((option) => !option.isDeprecated);

  const getOptionFromMultiValue = (value: number[]) => {
    const candidates = options.filter((option) => value.includes(option.value as number));
    return candidates;
  };

  const getReadOnlyLabel = (value: any) => {
    switch (true) {
      case !isNaN(Number(value)):
        return Object.values(FeedbackPerformed)[value];
      case value === 'no':
        return t('orders.OPTION_SPECIFIC_FEEDBACK_RECIPIENT_NO');
      case value === 'yes':
        return t('orders.OPTION_SPECIFIC_FEEDBACK_RECIPIENT_YES');
      default:
        return value;
    }
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={rules || {}}
      render={({ field, fieldState, formState }) => {
        const [inputValue, setInputValue] = useState('');

        let className: string = '';

        if (!fieldState.invalid && (fieldState.isTouched || formState.isSubmitted)) {
          className = 'dropdown is-valid';
        } else if (fieldState.invalid && (fieldState.isTouched || formState.isSubmitted)) {
          className = 'dropdown is-invalid';
        }

        if (readonly) {
          className += ' is-read-only';
        }

        if (readonly) {
          if (isMulti) {
            const fieldValue = field.value
              .map((i) => {
                const value = getOptionFromValue(i)?.label;
                return value || i;
              })
              .join(', ');
            return (
              <>
                <span>{label}</span>
                <Input readOnly value={fieldValue} type={'textarea'} disabled={readonly} />
              </>
            );
          } else {
            const option: IOption<TOption> | undefined = getOptionFromValue(
              field.value.id as IEntityReference
            );
            const value = getReadOnlyLabel(field.value);
            return (
              <>
                <span>{label}</span>
                <Input readOnly value={option ? option.label : t(value)} disabled={readonly} />
              </>
            );
          }
        }

        let component = isMulti ? (
          <>
            <span>{label}</span>
            <Select<IOption<TOption>, boolean>
              isMulti={isMulti}
              getOptionLabel={getOptionLabel}
              getOptionValue={getOptionValue}
              placeholder={placeholder}
              classNamePrefix="drop-"
              name={field.name}
              ref={field.ref}
              value={getOptionFromMultiValue(field.value)}
              onBlur={field.onBlur}
              onChange={(e) => {
                field.onChange(
                  !!e ? (e as MultiValue<IOption<TOption>>).map((p: any) => p.value) : undefined
                );
              }}
              className={className}
              options={
                hideOptionsUntilSearch
                  ? inputValue.length > 0
                    ? availableOptions
                    : []
                  : availableOptions
              } // Show options only if user has typed
              onInputChange={(newValue) => setInputValue(newValue)} // Track user input
              isDisabled={readonly}
            />
          </>
        ) : (
          <>
            <span>{label}</span>
            <Select<IOption<TOption>>
              getOptionLabel={getOptionLabel}
              getOptionValue={getOptionValue}
              placeholder={placeholder}
              classNamePrefix="drop-"
              name={field.name}
              ref={field.ref}
              value={getOptionFromValue(field.value)}
              onBlur={field.onBlur}
              onChange={(e) => {
                field.onChange(e ? (isOnlyValue ? e.value : { id: e.value, name: e.label }) : '');
              }}
              className={className}
              options={
                hideOptionsUntilSearch
                  ? inputValue.length > 0
                    ? availableOptions
                    : []
                  : availableOptions
              } // Show options only if user has typed
              onInputChange={(newValue) => setInputValue(newValue)} // Track user input
              isDisabled={readonly}
            />
          </>
        );
        return component;
      }}
    />
  );
}
